import { API_BASE_URL } from "../config/environment";

const handleError = async (response) => {
	let errorMessage = "Unknown error";
	if (response) {
		try {
			const errorData = await response.json();
			errorMessage = errorData.message || response.statusText;
		} catch (e) {
			errorMessage = response.statusText;
		}
	}
	console.error(`HTTP error: ${response.status} ${errorMessage}`);
	throw new Error(errorMessage);
};
const fetchApi = async (url, options = {}) => {
	let token = localStorage.getItem("token"); // Retrieve the token from local storage
	// FormData must keep the browser-generated multipart boundary: forcing
	// application/json here would make the server read an empty form.
	const isFormData =
		typeof FormData !== "undefined" && options.body instanceof FormData;
	let headers = {
		...(isFormData ? {} : { "Content-Type": "application/json" }),
		...options.headers,
	};

	if (token) {
		headers["Authorization"] = `Bearer ${token}`;
	}

	try {
		let response = await fetch(`${API_BASE_URL}${url}`, {
			...options,
			credentials: "include",
			headers, // Ensures cookies or credentials are sent
		});

		if (response.status === 401) {
			// If unauthorized, try to refresh the token

			token = await refreshAccessTokenLocked();

			headers["Authorization"] = `Bearer ${token}`;
			response = await fetch(`${API_BASE_URL}${url}`, {
				...options,
				credentials: "include",
				headers, // Ensures cookies or credentials are sent
			});
		}

		if (!response.ok) {
			await handleError(response);
		}
		const text = await response.text();
		return text ? JSON.parse(text) : {};
	} catch (error) {
		console.error("Fetch error:", error);
		throw error;
	}
};

export const setPhoto = async (
	photoFile,
	userId,
	fileName,
	isMain,
	photoId,
	progressCallback,
) => {
	if (!photoFile) throw new Error("No video file provided.");
	if (!userId || userId <= 0) throw new Error("Invalid user ID.");
	if (!fileName) throw new Error("File name is missing.");

	// 1) Делаем «снимок» файла в памяти
	//    Больше НИКОГДА не читаем оригинальный File, только этот Blob.
	const buffer = await photoFile.arrayBuffer();
	const snapshotBlob = new Blob([buffer], {
		type: photoFile.type || "application/octet-stream",
	});

	const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB
	const totalChunks = Math.ceil(snapshotBlob.size / CHUNK_SIZE);
	const uploadId =
		typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random()}`;

	const uploadChunkWithRetry = async (formData, chunkIndex, maxRetries = 3) => {
		let attempt = 0;
		while (true) {
			try {
				await new Promise((resolve, reject) => {
					const xhr = new XMLHttpRequest();
					xhr.open("POST", `${API_BASE_URL}/photos/set`, true);

					const token = localStorage.getItem("token");
					if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

					xhr.upload.onprogress = (event) => {
						if (
							event.lengthComputable &&
							typeof progressCallback === "function"
						) {
							const chunkProgress = (event.loaded / event.total) * 100;
							const overallProgress =
								((chunkIndex + chunkProgress / 100) / totalChunks) * 100;
							progressCallback(Math.min(overallProgress, 100));
						}
					};

					xhr.onload = () => {
						if (xhr.status >= 200 && xhr.status < 300) resolve();
						else {
							const error = new Error(
								`Failed to upload chunk ${chunkIndex}: ${xhr.status} ${xhr.statusText}`,
							);
							error.status = xhr.status;
							reject(error);
						}
					};

					xhr.onerror = () =>
						reject(new Error(`Network error uploading chunk ${chunkIndex}`));

					xhr.send(formData);
				});
				return;
			} catch (err) {
				attempt++;
				if (attempt > maxRetries) throw err;
				await new Promise((r) => setTimeout(r, 300 * Math.pow(2, attempt - 1)));
			}
		}
	};

	try {
		// 2) Режем ТОЛЬКО snapshotBlob
		for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
			const start = chunkIndex * CHUNK_SIZE;
			const end = Math.min(start + CHUNK_SIZE, snapshotBlob.size);
			const chunk = snapshotBlob.slice(start, end);

			const formData = new FormData();
			formData.append("chunk", chunk);
			formData.append("chunkIndex", chunkIndex);
			formData.append("totalChunks", totalChunks);
			formData.append("uploadId", uploadId);
			formData.append("fileName", fileName);
			formData.append("userId", String(userId));

			await uploadChunkWithRetry(formData, chunkIndex);
		}

		if (typeof progressCallback === "function") progressCallback(100);

		// 3) Финализация
		const data = await fetchApi(
			`/photos/finalize-upload?userId=${userId}&fileName=${encodeURIComponent(
				fileName,
			)}&isMain=${isMain}&photoId=${photoId}`,
			{ method: "POST" },
		);

		return data;
	} catch (error) {
		console.error("Upload photo error:", error);
		throw error;
	}
};
const onRefreshFailed = () => {
	localStorage.removeItem("token");
	localStorage.removeItem("refreshToken");
	// Hard redirect: this module has no access to the router.
	if (window.location.pathname !== "/login") {
		window.location.assign("/login");
	}
};

const refreshAccessToken = async () => {
	const token = localStorage.getItem("token");
	const refreshToken = localStorage.getItem("refreshToken");
	if (!refreshToken) {
		onRefreshFailed();
		throw new Error("No refresh token available");
	}

	try {
		// Backend `[HttpPut("refresh")]` — PUT, not POST.
		const response = await fetch(`${API_BASE_URL}/adminuser/refresh`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ token, refreshToken }),
		});

		if (!response.ok) {
			throw new Error("Failed to refresh token");
		}

		const data = await response.json();
		localStorage.setItem("token", data.token); // Save new access token
		localStorage.setItem("refreshToken", data.refreshToken); // Save new refresh token

		return data.token;
	} catch (error) {
		console.error("Error refreshing token:", error);
		onRefreshFailed();
		throw error;
	}
};
let refreshPromise = null;

const refreshAccessTokenLocked = () => {
	if (!refreshPromise) {
		refreshPromise = refreshAccessToken().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
};
// User login
export const loginUser = async (loginDTO) => {
	try {
		const response = await fetchApi("/adminuser/login", {
			method: "POST",
			body: JSON.stringify(loginDTO),
		});

		if (response.token) {
			localStorage.setItem("token", response.token); // Save the access token
			localStorage.setItem("refreshToken", response.refreshToken); // Save the refresh token
		}
		return response;
	} catch (error) {
		console.error("Login error:", error);
		throw error;
	}
};

export const getUserFirstUseAll = async (userId) => {
	try {
		let url = `/UserFirstUse/getAll?userId=${userId}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get error:", error);
		throw error;
	}
};

export const getUserAnalytics = async (fromDate) => {
	try {
		// Construct the URL with fromDate and toDate query parametersnpm
		const params = new URLSearchParams();
		if (fromDate) params.append("fromDate", fromDate);

		const url = `/moderation/getModeratedAnalytics${
			params.toString() ? `?${params.toString()}` : ""
		}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get analytics error:", error);
		throw error;
	}
};

export const deleteFirstUse = async (id) => {
	try {
		const response = await fetchApi("/UserFirstUse/delete", {
			method: "POST",
			body: JSON.stringify({ id }),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};

export const getAdminUser = async () => {
	try {
		const response = await fetchApi("/adminuser/getAdminUser", {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};

export const getAllAdminUsers = async () => {
	try {
		const response = await fetchApi("/adminuser/all", {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getAllUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getAll?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getNewUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getNew?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getEditUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getEdit?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};

export const getUsersMod = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
	status = 4,
) => {
	try {
		let url = `/moderation/getMod?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}&status=${status}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};

export const getUsersModDetails = async (userId) => {
	try {
		let url = `/moderation/getModDetails?userId=${userId}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};

export const getVideo = async (userId) => {
	try {
		const response = await fetchApi(`/moderation/video/${userId}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get video error:", error);
		throw error;
	}
};

export const getPhotos = async (userId) => {
	try {
		const response = await fetchApi(`/moderation/photos/${userId}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get video error:", error);
		throw error;
	}
};

export const getSelfie = async (userId) => {
	try {
		const response = await fetchApi(`/moderation/selfie/${userId}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get video error:", error);
		throw error;
	}
};
export const getOrders = async () => {
	try {
		const response = await fetchApi(`/orders/all`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get video error:", error);
		throw error;
	}
};

export const getUserOrders = async (userId) => {
	try {
		const response = await fetchApi(`/userorders/${userId}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get video error:", error);
		throw error;
	}
};

export const setOrders = async (updateUserDTO) => {
	try {
		const response = await fetchApi("/orders/update", {
			method: "POST",
			body: JSON.stringify(updateUserDTO),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const getFailUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getFail?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getFreezedUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getFreezed?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getDellUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getDell?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
// `getDelCounts` removed — backend doesn't expose /moderation/getDelCounts,
// no caller in the moderation pages, leftover from a deprecated flow.
export const getTBANKData = async () => {
	try {
		const url = `/moderation/getTBANKData`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Check error:", error);
		throw error;
	}
};
export const getInfo = async () => {
	try {
		const url = `/info`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Check error:", error);
		throw error;
	}
};

export const getMonthly = async () => {
	try {
		const url = `/info/monthly`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Check error:", error);
		throw error;
	}
};

export const getDaily = async (fromDate, toDate) => {
	try {
		const url = `/info/daily?fromDate=${fromDate}&toDate=${toDate}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Check error:", error);
		throw error;
	}
};

export const getAllGPTUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	hasActiveOrder = null,
) => {
	try {
		// Use URLSearchParams for proper URL encoding and handling
		const params = new URLSearchParams({
			pageNumber: pageNumber.toString(),
			pageSize: pageSize.toString(),
			searchTerm: searchTerm || "", // Ensure empty string if null/undefined
			...(hasActiveOrder !== null && {
				hasActiveOrder: hasActiveOrder.toString(),
			}), // Only include if not null
		});

		const url = `/UsersGPT/all?${params.toString()}`;

		const response = await fetchApi(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json", // Good to specify even for GET
			},
		});

		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error; // Re-throw to allow caller to handle
	}
};
export const getBlockUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) => {
	try {
		let url = `/moderation/getBlocked?sex=${sex}&country=${country}&city=${city}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const getCounts = async () => {
	try {
		let url = `/moderation/getCounts`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get all users error:", error);
		throw error;
	}
};
export const deleteUser = async (userId, reasonId = null, customReason = null) => {
	try {
		const response = await fetchApi("/moderation/deleteUser", {
			method: "POST",
			body: JSON.stringify({ userId, reasonId, customReason }),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};
// `rejectUser` removed — backend doesn't expose /moderation/rejectUser, no
// caller in the moderation pages. Use `deleteUser` for that flow.
export const confirmUser = async (userId) => {
	try {
		const response = await fetchApi("/moderation/confirmUser", {
			method: "POST",
			body: JSON.stringify(userId),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};
export const blockUser = async (data) => {
	try {
		const response = await fetchApi("/moderation/blockUser", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};
export const unBlockUser = async (data) => {
	try {
		const response = await fetchApi("/moderation/unBlockUser", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};
export const deleteAdminUser = async (deleteDTO) => {
	try {
		// Backend `[HttpDelete("delete")]`.
		const response = await fetchApi("/adminuser/delete", {
			method: "DELETE",
			body: JSON.stringify(deleteDTO),
		});
		return response;
	} catch (error) {
		console.error("Delete error:", error);
		throw error;
	}
};
export const registerAdminUser = async (registerDTO) => {
	try {
		// Backend `[HttpPost("register")]`.
		const response = await fetchApi("/adminuser/register", {
			method: "POST",
			body: JSON.stringify(registerDTO),
		});
		return response;
	} catch (error) {
		console.error("Register admin error:", error);
		throw error;
	}
};
export const updateAdminUser = async (deleteDTO) => {
	try {
		// Backend `[HttpPut("update")]`.
		const response = await fetchApi("/adminuser/update", {
			method: "PUT",
			body: JSON.stringify(deleteDTO),
		});
		return response;
	} catch (error) {
		console.error("Update error:", error);
		throw error;
	}
};

export const updateUser = async (updateUserDTO) => {
	try {
		const response = await fetchApi("/moderation/update", {
			method: "POST",
			body: JSON.stringify(updateUserDTO),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const aproovePhoto = async (id) => {
	try {
		const response = await fetchApi("/moderation/aproovePhoto", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const aprooveSelfie = async (id) => {
	try {
		const response = await fetchApi("/moderation/aprooveSelfie", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const aprooveVideo = async (id) => {
	try {
		const response = await fetchApi("/moderation/aprooveVideo", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const deletePhoto = async (id, reasonId = null, customReason = null) => {
	try {
		const response = await fetchApi("/moderation/deletePhoto", {
			method: "POST",
			body: JSON.stringify({ id, reasonId, customReason }),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};
export const deleteVideo = async (id) => {
	try {
		const response = await fetchApi("/moderation/deleteVideo", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};
export const deleteSelfie = async (id, reasonId = null, customReason = null) => {
	try {
		const response = await fetchApi("/moderation/deleteSelfie", {
			method: "POST",
			body: JSON.stringify({ id, reasonId, customReason }),
		});
		return response;
	} catch (error) {
		console.error("Update user error:", error);
		throw error;
	}
};

export const sendToTelegram = async (data) => {
	try {
		const response = await fetchApi("/telegram/send-notification", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response;
	} catch (error) {
		console.error("Massage send failure:", error);
		throw error;
	}
};
export const sendToAllTelegram = async (data) => {
	try {
		const response = await fetchApi("/telegram/sendToAll", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response;
	} catch (error) {
		console.error("Massage send failure:", error);
		throw error;
	}
};
export const sendToAllGPTTelegram = async (data) => {
	try {
		// No separate /TelegramGPTNik route exists — the unified `/telegram/sendToAll`
		// dispatches by `data.type` (100 = GPT users) on the backend.
		const response = await fetchApi("/telegram/sendToAll", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response;
	} catch (error) {
		console.error("GPT broadcast send failure:", error);
		throw error;
	}
};

export const getSMSCounts = async (type) => {
	try {
		let url = `/smscounts/getCounts/${type}`;
		const response = await fetchApi(url, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get Referrals error:", error);
		throw error;
	}
};

// `sendReferrals` removed — backend doesn't expose /telegram/sendRefs/{userId},
// no caller in the moderation pages.


export const adminGrant = async (grantRequest) => {
	const response = await fetchApi("/AdminGrants", {
		method: "POST",
		body: JSON.stringify(grantRequest),
	});
	return response;
};

export const endOrders = async (userId) => {
	try {
		const response = await fetchApi(`/UserOrders/end?userId=${userId}`, {
			method: "POST",
		});

		return response;
	} catch (error) {
		console.error("Upload video error:", error);
		throw error;
	}
};

// export const userIsVip = async (userId) => {
// 	try {
// 		let url = `/reactions/getUserUndo/${userId}`;
// 		const response = await fetchApi(url, {
// 			method: "GET",
// 		});
// 		return response;
// 	} catch (error) {
// 		console.error("Get state error:", error);
// 		throw error;
// 	}
// };

// Устаревшая ручка: отдаёт все жалобы за всё время вместе с текстами.
// Экран «Жалобы» ходит в getReportSummaries/getUserReportDetails; оставлена
// ради совместимости со старыми клиентами.
export const getReports = async (searchTerm) => {
	try {
		const params = new URLSearchParams();
		if (searchTerm) params.append("searchTerm", searchTerm);
		const url = `/reports/all${
			params.toString() ? `?${params.toString()}` : ""
		}`;
		const response = await fetchApi(url, { method: "GET" });
		return response;
	} catch (error) {
		console.error("Check error:", error);
		throw error;
	}
};

// Страница списка «Жалобы»: одна строка на пользователя, без текстов жалоб.
export const getReportSummaries = async ({
	pageNumber = 1,
	pageSize = 20,
	searchTerm,
} = {}) => {
	try {
		const params = new URLSearchParams();
		params.set("pageNumber", pageNumber);
		params.set("pageSize", pageSize);
		if (searchTerm) params.set("searchTerm", searchTerm);

		return await fetchApi(`/reports/summary?${params.toString()}`, {
			method: "GET",
		});
	} catch (error) {
		console.error("Get report summaries error:", error);
		throw error;
	}
};

// Тексты жалоб на одного пользователя — грузятся по клику на строку.
export const getUserReportDetails = async (userId) => {
	try {
		return await fetchApi(`/reports/user/${userId}/details`, {
			method: "GET",
		});
	} catch (error) {
		console.error("Get user report details error:", error);
		throw error;
	}
};

export const recomputeFaceMatch = async (userId) => {
	if (!userId || Number(userId) <= 0) {
		throw new Error("Invalid userId");
	}
	return fetchApi(`/Moderation/face-match/${userId}`, {
		method: "POST",
	});
};

const DELETE_REQUESTS_ENDPOINT = "/Moderation/deleteRequests";
const SUPPORT_MESSAGES_ENDPOINT = "/Moderation/supportMessages";

export const getDeleteRequests = async ({
	pageNumber = 1,
	pageSize = 20,
	searchTerm,
	status,
} = {}) => {
	try {
		const params = new URLSearchParams();

		params.set("pageNumber", pageNumber);
		params.set("pageSize", pageSize);

		if (searchTerm) params.set("searchTerm", searchTerm);
		if (status) params.set("status", status);

		const response = await fetchApi(
			`${DELETE_REQUESTS_ENDPOINT}?${params.toString()}`,
			{
				method: "GET",
			},
		);
		return response;
	} catch (error) {
		console.error("Get delete requests error:", error);
		throw error;
	}
};

export const getSupportMessages = async () => {
	try {
		const response = await fetchApi(SUPPORT_MESSAGES_ENDPOINT, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get support messages error:", error);
		throw error;
	}
};

const SUPPORT_DIALOGS_ENDPOINT = "/Moderation/supportDialogs";

export const getSupportDialogs = async ({
	pageNumber = 1,
	pageSize = 50,
	searchTerm,
} = {}) => {
	try {
		const params = new URLSearchParams();

		params.set("pageNumber", pageNumber);
		params.set("pageSize", pageSize);

		if (searchTerm) params.set("searchTerm", searchTerm);

		const response = await fetchApi(
			`${SUPPORT_DIALOGS_ENDPOINT}?${params.toString()}`,
			{
				method: "GET",
			},
		);
		return response;
	} catch (error) {
		console.error("Get support dialogs error:", error);
		throw error;
	}
};

export const getSupportDialogMessages = async (
	userId,
	{ pageNumber = 1, pageSize = 100 } = {},
) => {
	try {
		const params = new URLSearchParams();

		params.set("pageNumber", pageNumber);
		params.set("pageSize", pageSize);

		const response = await fetchApi(
			`${SUPPORT_DIALOGS_ENDPOINT}/${userId}/messages?${params.toString()}`,
			{
				method: "GET",
			},
		);
		return response;
	} catch (error) {
		console.error("Get support dialog messages error:", error);
		throw error;
	}
};

// `attachmentKey`/`attachmentType` come from uploadSupportDialogAttachment and
// are optional: a text-only reply behaves exactly as before.
export const replySupportDialog = async (
	userId,
	message,
	{ attachmentKey, attachmentType } = {},
) => {
	try {
		const body = { message };
		if (attachmentKey) {
			body.attachmentKey = attachmentKey;
			body.attachmentType = attachmentType || "image";
		}

		const response = await fetchApi(
			`${SUPPORT_DIALOGS_ENDPOINT}/${userId}/reply`,
			{
				method: "POST",
				body: JSON.stringify(body),
			},
		);
		return response;
	} catch (error) {
		console.error("Reply support dialog error:", error);
		throw error;
	}
};

// Uploads a photo for an admin reply. The key is minted under the dialog owner,
// so the user is allowed to read what we send them.
export const uploadSupportDialogAttachment = async (userId, file) => {
	try {
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetchApi(
			`${SUPPORT_DIALOGS_ENDPOINT}/${userId}/attachments`,
			{
				method: "POST",
				body: formData,
			},
		);
		return response;
	} catch (error) {
		console.error("Upload support dialog attachment error:", error);
		throw error;
	}
};

// Rewrites the text of an already-sent admin reply.
export const editSupportDialogMessage = async (userId, messageId, message) => {
	try {
		const response = await fetchApi(
			`${SUPPORT_DIALOGS_ENDPOINT}/${userId}/messages/${messageId}`,
			{
				method: "PUT",
				body: JSON.stringify({ message }),
			},
		);
		return response;
	} catch (error) {
		console.error("Edit support dialog message error:", error);
		throw error;
	}
};

const CHAT_MODERATION_ENDPOINT = "/chat-moderation";

export const getUserChatConversations = async ({
	pageNumber = 1,
	pageSize = 30,
	searchTerm,
	suspiciousOnly,
} = {}) => {
	try {
		const params = new URLSearchParams();
		params.set("pageNumber", pageNumber);
		params.set("pageSize", pageSize);
		if (searchTerm) params.set("searchTerm", searchTerm);
		if (suspiciousOnly) params.set("suspiciousOnly", "true");

		const response = await fetchApi(
			`${CHAT_MODERATION_ENDPOINT}/conversations?${params.toString()}`,
			{ method: "GET" },
		);
		return response;
	} catch (error) {
		console.error("Get user chat conversations error:", error);
		throw error;
	}
};

export const getUserChatMessages = async (
	conversationId,
	{ beforeMessageId, pageSize = 50 } = {},
) => {
	try {
		const params = new URLSearchParams();
		params.set("pageSize", pageSize);
		if (beforeMessageId) params.set("beforeMessageId", beforeMessageId);

		const response = await fetchApi(
			`${CHAT_MODERATION_ENDPOINT}/conversations/${conversationId}/messages?${params.toString()}`,
			{ method: "GET" },
		);
		return response;
	} catch (error) {
		console.error("Get user chat messages error:", error);
		throw error;
	}
};

export const getSuspiciousWords = async ({ includeInactive = false } = {}) => {
	try {
		const params = new URLSearchParams();
		if (includeInactive) params.set("includeInactive", "true");

		const response = await fetchApi(
			`${CHAT_MODERATION_ENDPOINT}/suspicious-words?${params.toString()}`,
			{ method: "GET" },
		);
		return response;
	} catch (error) {
		console.error("Get suspicious words error:", error);
		throw error;
	}
};

export const addSuspiciousWord = async (word) => {
	try {
		const response = await fetchApi(
			`${CHAT_MODERATION_ENDPOINT}/suspicious-words`,
			{ method: "POST", body: JSON.stringify({ word }) },
		);
		return response;
	} catch (error) {
		console.error("Add suspicious word error:", error);
		throw error;
	}
};

export const deleteSuspiciousWord = async (id) => {
	try {
		await fetchApi(`${CHAT_MODERATION_ENDPOINT}/suspicious-words/${id}`, {
			method: "DELETE",
		});
		return true;
	} catch (error) {
		console.error("Delete suspicious word error:", error);
		throw error;
	}
};

export const markReportViewed = async (reportId) => {
	try {
		// Backend binds `[FromBody] long reportId` — must be a JSON number, not
		// a raw value (raw fetch body string-coerces and can fail binding).
		const response = await fetchApi(`/reports/viewed`, {
			method: "POST",
			body: JSON.stringify(reportId),
		});
		return response;
	} catch (error) {
		console.error("Mark report viewed error:", error);
		throw error;
	}
};

export const getUserChanges = async (userId) => {
	try {
		const response = await fetchApi(`/moderation/changes/${userId}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get user changes error:", error);
		throw error;
	}
};

export const aprooveChange = async (id) => {
	try {
		const response = await fetchApi("/moderation/aprooveChange", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Approve change error:", error);
		throw error;
	}
};

export const delChange = async (id) => {
	try {
		const response = await fetchApi("/moderation/delChange", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Delete(reject) change error:", error);
		throw error;
	}
};

// --- Brought over from legacy jerm-moderation (Broadcasts + admin pay-link + refund) ---

export const getBroadcasts = async (take = 50, skip = 0) => {
	return await fetchApi(`/Broadcasts/list?take=${take}&skip=${skip}`, {
		method: "GET",
	});
};

export const getBroadcastDetails = async (id) => {
	return await fetchApi(`/Broadcasts/${id}`, { method: "GET" });
};

export const resumeBroadcast = async (id) => {
	return await fetchApi(`/Broadcasts/${id}/resume`, { method: "POST" });
};

export const cancelBroadcast = async (id) => {
	return await fetchApi(`/Broadcasts/${id}/cancel`, { method: "POST" });
};

export const deleteBroadcastMessages = async (id) => {
	return await fetchApi(`/Broadcasts/${id}/delete-messages`, { method: "POST" });
};

// --- Рассылка внутри приложения (чат от служебного профиля Jerm) ---
// Телеграмная рассылка доходит только до тех, у кого привязан бот и он не
// заблокирован. Эта — до всех, потому что кладётся в чат.

export const previewAppBroadcastAudience = async (audienceType, sex) => {
	return await fetchApi("/Broadcasts/app/preview", {
		method: "POST",
		body: JSON.stringify({ audienceType, sex }),
	});
};

/**
 * Кладёт сообщение в чат от профиля Jerm. Необязательные поля отвечают за
 * «звонок в дверь» — уведомление о той же рассылке: сервер считает их
 * отсутствие согласием на пуш, поэтому announceByPush передаётся всегда, когда
 * выбор сделан осознанно.
 */
export const createAppBroadcast = async (
	audienceType,
	sex,
	message,
	options = {},
) => {
	const payload = { audienceType, sex, message };

	if (options.announceByPush !== undefined) {
		payload.announceByPush = options.announceByPush;
	}
	if (options.pushTitle !== undefined) {
		payload.pushTitle = options.pushTitle;
	}
	if (options.pushBody !== undefined) {
		payload.pushBody = options.pushBody;
	}
	// Картинка — ссылка и путь из ответа /Broadcasts/push/image. Ссылку сервер
	// отдаёт клиенту как есть, путь нужен ему, чтобы убрать файл при отзыве
	// рассылки. Пусто не передаём вовсе: запрос без картинки должен выглядеть
	// ровно так же, как раньше.
	if (options.imageUrl) {
		payload.imageUrl = options.imageUrl;
	}
	if (options.imageKey) {
		payload.imageKey = options.imageKey;
	}

	return await fetchApi("/Broadcasts/app", {
		method: "POST",
		body: JSON.stringify(payload),
	});
};

/**
 * Пробная отправка объявления одному человеку.
 *
 * Создаёт настоящую строку рассылки с одним получателем — в чате она выглядит
 * ровно так, как увидят все. В историю рассылок такая строка не попадает (как и
 * пробное уведомление), но отзывается обычным deleteAppBroadcast по id из
 * ответа.
 *
 * Ключ картинки не передаётся намеренно: отзыв рассылки сносит файл из бакета,
 * и проба унесла бы картинку, которую ещё ждёт настоящая отправка.
 *
 * Ответ: { broadcastId, userId, status, ok, minAppVersion, appVersions, web,
 * imageUrl, createdAt }, где status — visible | outdated_app | unknown.
 */
export const sendTestAppBroadcast = async (userId, message, imageUrl = null) => {
	return await fetchApi("/Broadcasts/app/test", {
		method: "POST",
		body: JSON.stringify({ userId, message, imageUrl }),
	});
};

/**
 * История рассылок в приложении. includeTests добавляет к ней пробные отправки:
 * без этого забытую пробу нечем найти и отозвать — её id виден только в ответе
 * самой пробы. По умолчанию список выглядит ровно как прежде.
 */
export const getAppBroadcasts = async (take = 50, skip = 0, includeTests = false) => {
	const query = `take=${take}&skip=${skip}${includeTests ? "&includeTests=true" : ""}`;
	return await fetchApi(`/Broadcasts/app/list?${query}`, {
		method: "GET",
	});
};

// Рассылка хранится одной строкой, поэтому отзывается у всех разом.
// Вместе с ней снимаются неотправленные уведомления о ней же — сервер
// возвращает { ok, cancelledPushes }.
export const deleteAppBroadcast = async (id) => {
	return await fetchApi(`/Broadcasts/app/${id}`, { method: "DELETE" });
};

// --- Рассылка пушами (мобильное приложение и браузер) ---
// В отличие от рассылки в приложении уведомление доходит только до тех, у кого
// есть живая подписка, поэтому охват считается отдельно и до отправки.

/**
 * Охват по каналам: сколько человек в аудитории и сколько из них реально
 * получит уведомление. linkedToAppBroadcast сужает мобильную часть — нажатие на
 * такой пуш открывает чат от профиля Jerm, а это умеет не всякая сборка.
 */
export const previewPushBroadcastReach = async (
	audienceType,
	sex,
	linkedToAppBroadcast,
) => {
	return await fetchApi("/Broadcasts/push/preview", {
		method: "POST",
		body: JSON.stringify({ audienceType, sex, linkedToAppBroadcast }),
	});
};

export const createPushBroadcast = async (
	audienceType,
	sex,
	title,
	body,
	appBroadcastId = null,
	imageUrl = null,
) => {
	return await fetchApi("/Broadcasts/push", {
		method: "POST",
		body: JSON.stringify({
			audienceType,
			sex,
			title,
			body,
			appBroadcastId,
			imageUrl,
		}),
	});
};

/**
 * Пробная отправка одному человеку: настоящее уведомление, тот же путь, что и у
 * массовой отправки, но ответ приходит сразу.
 *
 * channel — mobile | web | both, либо null. Пусто означает «как у массовой
 * рассылки»: канал выберет сервер, а приложение важнее браузера. Явный both
 * нужен именно проверяющему: правило «одно уведомление на человека» иначе
 * скрывает ровно ту поверхность, которую и хотели увидеть.
 */
export const sendTestPushBroadcast = async (
	userId,
	title,
	body,
	appBroadcastId = null,
	channel = null,
	imageUrl = null,
) => {
	return await fetchApi("/Broadcasts/push/test", {
		method: "POST",
		body: JSON.stringify({
			userId,
			title,
			body,
			appBroadcastId,
			channel,
			imageUrl,
		}),
	});
};

/**
 * Загружает картинку для уведомления и возвращает
 * { imageKey, imageUrl, support }. Ссылку из ответа нужно передать в imageUrl
 * пробной или массовой отправки. support говорит, где картинка действительно
 * появится: на iPhone её не будет, и это не сбой отправки.
 *
 * Тело — FormData, поэтому fetchApi сам не ставит application/json: иначе
 * пропадёт граница multipart и сервер получит пустую форму.
 */
export const uploadPushBroadcastImage = async (file) => {
	const formData = new FormData();
	formData.append("file", file);
	return await fetchApi("/Broadcasts/push/image", {
		method: "POST",
		body: formData,
	});
};

export const getPushBroadcasts = async (take = 50, skip = 0) => {
	return await fetchApi(`/Broadcasts/push/list?take=${take}&skip=${skip}`, {
		method: "GET",
	});
};

export const getPushBroadcast = async (id) => {
	return await fetchApi(`/Broadcasts/push/${id}`, { method: "GET" });
};

export const adminGeneratePayLink = async (userId, orderId) => {
	return await fetchApi("/TBank/admin/pay", {
		method: "POST",
		body: JSON.stringify({ userId, orderId }),
	});
};

export const refundUserOrder = async (userOrderId) => {
	return await fetchApi("/TBank/refund", {
		method: "POST",
		body: JSON.stringify({ userOrderId }),
	});
};

// ---- Jerm Cupid (game) ----

export const getCupidSeasons = async () => {
	const response = await fetchApi("/jermcupid/admin/seasons", { method: "GET" });
	return response;
};

export const upsertCupidSeason = async (seasonDTO) => {
	const response = await fetchApi("/jermcupid/admin/seasons", {
		method: "POST",
		body: JSON.stringify(seasonDTO),
	});
	return response;
};

export const deleteCupidSeason = async (seasonId) => {
	const response = await fetchApi(`/jermcupid/admin/seasons/${seasonId}`, {
		method: "DELETE",
	});
	return response;
};

export const getCupidPrizes = async (seasonId) => {
	const query = seasonId ? `?seasonId=${seasonId}` : "";
	const response = await fetchApi(`/jermcupid/admin/prizes${query}`, { method: "GET" });
	return response;
};

export const upsertCupidPrize = async (prizeDTO) => {
	const response = await fetchApi("/jermcupid/admin/prizes", {
		method: "POST",
		body: JSON.stringify(prizeDTO),
	});
	return response;
};

export const deleteCupidPrize = async (prizeId) => {
	const response = await fetchApi(`/jermcupid/admin/prizes/${prizeId}`, {
		method: "DELETE",
	});
	return response;
};

export const getCupidAdminLeaderboard = async (seasonId, type) => {
	const params = new URLSearchParams();
	if (seasonId) params.set("seasonId", String(seasonId));
	if (type) params.set("type", type);
	const response = await fetchApi(`/jermcupid/admin/leaderboard?${params.toString()}`, { method: "GET" });
	return response;
};

export const getCupidWinners = async (seasonId) => {
	const query = seasonId ? `?seasonId=${seasonId}` : "";
	const response = await fetchApi(`/jermcupid/admin/winners${query}`, { method: "GET" });
	return response;
};

export const markCupidWinnerGranted = async (winnerId, note) => {
	const response = await fetchApi(`/jermcupid/admin/winners/${winnerId}/mark-granted`, {
		method: "POST",
		body: JSON.stringify({ note: note || null }),
	});
	return response;
};

// ---- Deletion reason messages (profile/photo/selfie) ----

// Карточки свиданий на проверке: описание пишет человек, и до ленты его
// смотрит модератор — ровно как анкету и фотографии.
export const getDateCardsForModeration = async (take = 200) => {
	try {
		const response = await fetchApi(`/moderation/getDateCards?take=${take}`, {
			method: "GET",
		});
		return response;
	} catch (error) {
		console.error("Get date cards error:", error);
		throw error;
	}
};

export const aprooveDateCard = async (id) => {
	try {
		const response = await fetchApi("/moderation/aprooveDateCard", {
			method: "POST",
			body: JSON.stringify(id),
		});
		return response;
	} catch (error) {
		console.error("Aproove date card error:", error);
		throw error;
	}
};

export const deleteDateCard = async (id, reasonId = null, customReason = null) => {
	try {
		const response = await fetchApi("/moderation/deleteDateCard", {
			method: "POST",
			body: JSON.stringify({ id, reasonId, customReason }),
		});
		return response;
	} catch (error) {
		console.error("Delete date card error:", error);
		throw error;
	}
};

export const getDeleteReasons = async (category) => {
	const query = category ? `?category=${category}` : "";
	const response = await fetchApi(`/moderation/delete-reasons${query}`, {
		method: "GET",
	});
	return response;
};

export const addDeleteReason = async (data) => {
	const response = await fetchApi("/moderation/delete-reasons", {
		method: "POST",
		body: JSON.stringify(data),
	});
	return response;
};

export const updateDeleteReason = async (id, data) => {
	const response = await fetchApi(`/moderation/delete-reasons/${id}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
	return response;
};

export const deleteDeleteReason = async (id) => {
	const response = await fetchApi(`/moderation/delete-reasons/${id}`, {
		method: "DELETE",
	});
	return response;
};

// Media storage behaviour, stored in dbo.Config: where uploads are written,
// where deletes apply, how long a signed chat link lives, and the base URL the
// clients build photo URLs from.
export const getMediaSettings = async () => {
	const response = await fetchApi(`/moderation/mediaSettings`, {
		method: "GET",
	});
	return response;
};

export const setMediaSettings = async (settings) => {
	const response = await fetchApi(`/moderation/mediaSettings`, {
		method: "POST",
		body: JSON.stringify(settings),
	});
	return response;
};
// Translation engine used by translate.jerm.am ("google" / "yandex" / "chatgpt").
export const getTranslateProvider = async () => {
	const response = await fetchApi(`/moderation/translateProvider`, {
		method: "GET",
	});
	return response;
};

export const setTranslateProvider = async (provider) => {
	const response = await fetchApi(`/moderation/translateProvider`, {
		method: "POST",
		body: JSON.stringify({ provider }),
	});
	return response;
};

// Модель GPT для разбора совместимости ("gpt-4o", "gpt-4o-mini", ...).
export const getGptModel = async () => {
	const response = await fetchApi(`/moderation/gptModel`, {
		method: "GET",
	});
	return response;
};

export const setGptModel = async (model) => {
	const response = await fetchApi(`/moderation/gptModel`, {
		method: "POST",
		body: JSON.stringify({ model }),
	});
	return response;
};

// Модель GPT для перевода — отдельная настройка: разбор совместимости и перевод
// идут в разные сервисы. Пустая строка означает «модель берёт сам сервис
// перевода», и сервер тогда не передаёт ему модель вовсе.
export const getTranslateGptModel = async () => {
	const response = await fetchApi(`/moderation/translateGptModel`, {
		method: "GET",
	});
	return response;
};

export const setTranslateGptModel = async (model) => {
	const response = await fetchApi(`/moderation/translateGptModel`, {
		method: "POST",
		body: JSON.stringify({ model }),
	});
	return response;
};

// Проверка сохранённой модели живым вопросом. Модель сервер берёт из базы, в
// теле идёт только текст вопроса. Ответ диагностический:
// { model, source: "jerm" | "openai" | "none", content, jermError, openAiError,
//   jermMs, openAiMs } — важно видеть, что ответил не прокси, а OpenAI напрямую.
export const testGpt = async (prompt) => {
	const response = await fetchApi(`/gpt/test`, {
		method: "POST",
		body: JSON.stringify({ prompt }),
	});
	return response;
};

// Проверка переводчика: текст любым языком и код целевого языка. Исходный язык
// определяет сам сервис переводов, передавать его не нужно. Ответ
// диагностический: { text, provider, targetLang, ms, error } — provider тут тот,
// которым запрос реально ушёл (он кешируется на минуту).
export const testTranslate = async (text, targetLang) => {
	const response = await fetchApi(`/moderation/translateTest`, {
		method: "POST",
		body: JSON.stringify({ text, targetLang }),
	});
	return response;
};

// Отчёты бота об ошибках в Telegram: 0 — выключены, 1 — сразу, 2 — одной
// сводкой в 00:00.
export const getErrorReportMode = async () => {
	const response = await fetchApi(`/moderation/errorReportMode`, {
		method: "GET",
	});
	return response;
};

export const setErrorReportMode = async (mode) => {
	const response = await fetchApi(`/moderation/errorReportMode`, {
		method: "POST",
		body: JSON.stringify({ mode }),
	});
	return response;
};

// Ключи VAPID для web push. Наружу отдаётся только публичная половина пары и
// признак настроенности — приватный ключ остаётся на сервере.
export const getWebPushKeys = async () => {
	const response = await fetchApi(`/moderation/webPushKeys`, {
		method: "GET",
	});
	return response;
};

// Генерирует новую пару. Пустой subject означает значение по умолчанию на
// сервере. Перегенерация делает недействительными все подписки браузеров.
export const generateWebPushKeys = async (subject) => {
	const response = await fetchApi(`/moderation/webPushKeys`, {
		method: "POST",
		body: JSON.stringify({ subject }),
	});
	return response;
};

// Ключи Google по платформам. Приходят все четыре, включая ненастроенные:
// «ключа нет» — обычное состояние, а не отсутствие платформы.
export const getGoogleApiKeys = async () => {
	const response = await fetchApi("/google-api-keys");
	return Array.isArray(response) ? response : [];
};

// Пустое значение стирает ключ: у веба выключается карта, у сервера поиск мест
// уходит на запасной источник.
export const saveGoogleApiKey = async (payload) => {
	// payload: { platform, keyValue, note }
	const response = await fetchApi("/google-api-keys", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	return response;
};

// Content-Type у старых файлов в хранилище. Проверка ничего не меняет, ответ:
// { prefix, scanned, correct, wrong, unknownExtension, failed, scanComplete,
// sample[], failures[], finishedAtUtc }. Обход ограничен сверху: Content-Type в
// листинге не приходит, на каждый объект уходит отдельный запрос.
export const auditMediaContentType = async ({
	prefix = "",
	maxObjects = 0,
	sample = 0,
} = {}) => {
	const params = new URLSearchParams();
	if (prefix) params.set("prefix", prefix);
	if (maxObjects) params.set("maxObjects", String(maxObjects));
	if (sample) params.set("sample", String(sample));

	const query = params.toString();
	const response = await fetchApi(
		`/moderation/mediaContentType/audit${query ? `?${query}` : ""}`,
		{ method: "GET" },
	);
	return response;
};

// Запускает починку и сразу возвращает состояние прогона — сам прогон идёт в
// фоне на сервере и живёт дольше этого запроса: обход сотни тысяч объектов
// занимает минуты, столько живой запрос не держится. 409 означает «прогон уже
// идёт». Состояние: { state: idle|running|done|stopped|failed, prefix, scanned,
// repaired, skippedCorrect, skippedUnknownExtension, failed, scanComplete,
// stopRequested, lastKey, error, failures[], startedAtUtc, finishedAtUtc }.
export const startMediaContentTypeRepair = async ({ prefix = "" } = {}) => {
	const response = await fetchApi(`/moderation/mediaContentType/repair`, {
		method: "POST",
		body: JSON.stringify({ prefix }),
	});
	return response;
};

// Ход текущего прогона, а если его нет — итог последнего. Этот запрос админка
// повторяет раз в пару секунд, пока прогон идёт.
export const getMediaContentTypeRepairStatus = async () => {
	const response = await fetchApi(`/moderation/mediaContentType/status`);
	return response;
};

// Просит прогон остановиться. Безвредно: следующий запуск продолжит с места
// остановки сам — у починенного объекта тип уже верный, и в выборку он не
// попадает.
export const stopMediaContentTypeRepair = async () => {
	const response = await fetchApi(`/moderation/mediaContentType/stop`, {
		method: "POST",
	});
	return response;
};

// --- Legal documents (terms / privacy), edited on the Documents page ---

export const getLegalDocument = async (docType, lang) => {
	const response = await fetchApi(`/legal/${docType}/${lang}`);
	return response;
};

export const updateLegalDocument = async (docType, lang, payload) => {
	// payload: { title, bodyHtml, effectiveDate }
	const response = await fetchApi(`/legal/${docType}/${lang}`, {
		method: "PUT",
		body: JSON.stringify(payload),
	});
	return response;
};

export const publishLegalDocument = async (docType) => {
	const response = await fetchApi(`/legal/${docType}/publish`, {
		method: "POST",
	});
	return response;
};

export const getLegalHistory = async (docType) => {
	const response = await fetchApi(`/legal/${docType}/history`);
	return response;
};

export const getLegalHistoryEntry = async (id) => {
	const response = await fetchApi(`/legal/history/${id}`);
	return response;
};

export const rollbackLegalDocument = async (docType, version) => {
	const response = await fetchApi(`/legal/${docType}/rollback/${version}`, {
		method: "POST",
	});
	return response;
};

// --- Истории семей, раздел «Семьи, созданные в Jerm» ---

export const getFamilyStories = async () => {
	const response = await fetchApi("/family-stories/admin");
	return Array.isArray(response) ? response : [];
};

export const getFamilyStory = async (id) => {
	const response = await fetchApi(`/family-stories/admin/${id}`);
	return response;
};

export const saveFamilyStory = async (payload) => {
	// payload: { id?, sortOrder, isPublished, texts: [{ lang, names, meta, summary, body }] }
	const response = await fetchApi("/family-stories/admin", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	return response;
};

export const deleteFamilyStory = async (id) => {
	const response = await fetchApi(`/family-stories/admin/${id}`, {
		method: "DELETE",
	});
	return response;
};

export const addFamilyStoryPhoto = async (
	storyId,
	fileName,
	fileBase64,
	miniFileBase64 = null,
) => {
	const response = await fetchApi("/family-stories/admin/photo", {
		method: "POST",
		body: JSON.stringify({
			storyId,
			fileName,
			fileBase64,
			// Если прислано — бэкенд возьмёт этот кадр как источник обложки
			// карточки, иначе соберёт thumbnail из основного файла (см.
			// FamilyStoriesRepo.AddPhotoAsync).
			miniFileBase64,
		}),
	});
	return response;
};

export const deleteFamilyStoryPhoto = async (photoId) => {
	const response = await fetchApi(`/family-stories/admin/photo/${photoId}`, {
		method: "DELETE",
	});
	return response;
};

export const reorderFamilyStoryPhotos = async (storyId, photoIds) => {
	const response = await fetchApi("/family-stories/admin/photo/order", {
		method: "PUT",
		body: JSON.stringify({ storyId, photoIds }),
	});
	return response;
};

// ---- Идентификаторы внешних входов (Google, Apple) ----

export const getAuthProviders = async () => {
	const response = await fetchApi("/auth-providers");
	return Array.isArray(response) ? response : [];
};

export const saveAuthProvider = async (payload) => {
	// payload: { id?, provider, audience, platform, isEnabled, note }
	const response = await fetchApi("/auth-providers", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	return response;
};

export const deleteAuthProvider = async (id) => {
	const response = await fetchApi(`/auth-providers/${id}`, {
		method: "DELETE",
	});
	return response;
};
